import React from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { StaggerItem } from "@/lib/motion";
import { Stars } from "@/components/StarRating";
import { imgSrc } from "@/lib/img";

export const ProductCard = ({ product }) => {
  const { add } = useCart();
  const hasVariants = product.variants?.length > 0;
  const available = product.in_stock ?? (product.stock || "in_stock") === "in_stock";
  const price = product.price_from ?? product.final_price ?? product.price;
  const showStrike = product.discount_label && !hasVariants;
  return (
    <StaggerItem className="group">
      <div data-testid={`product-card-${product.id}`} className="bg-white rounded-2xl border border-forest/10 overflow-hidden hover:shadow-2xl hover:shadow-forest/10 hover:-translate-y-1.5 transition-all duration-500">
        <Link to={`/product/${product.id}`} data-testid={`product-link-${product.id}`} className="block relative aspect-[4/3] overflow-hidden bg-creamdark">
          <img src={imgSrc(product.image)} alt={product.name} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${available ? "" : "grayscale-[40%]"}`} />
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
            {product.badge && <span className="bg-forest text-cream text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full">{product.badge}</span>}
            {product.discount_label && <span data-testid={`product-discount-${product.id}`} className="bg-gold text-white text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full">{product.discount_label}</span>}
          </div>
          {!available && (
            <span data-testid={`product-stock-${product.id}`} className="absolute bottom-3 left-3 bg-cream/95 text-forest text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full">
              {product.stock === "coming_soon" ? "Coming soon" : "Sold out"}
            </span>
          )}
        </Link>
        <div className="p-5">
          <p className="text-[10px] uppercase tracking-[0.2em] text-gold font-bold">{product.collection}</p>
          <h3 className="font-serif text-xl text-forest mt-1.5"><Link to={`/product/${product.id}`} className="hover:text-gold transition-colors">{product.name}</Link></h3>
          {product.rating_count > 0 && (
            <span data-testid={`product-rating-${product.id}`} className="inline-flex items-center gap-1.5 mt-1.5">
              <Stars value={product.rating_avg} size={12} />
              <span className="text-[11px] text-mutedgreen">({product.rating_count})</span>
            </span>
          )}
          <p className="text-xs text-mutedgreen mt-1.5 leading-relaxed line-clamp-2">{product.tagline}</p>
          <div className="flex items-center justify-between mt-4 gap-3">
            <div>
              {hasVariants && <span className="text-[10px] uppercase tracking-wider text-mutedgreen mr-1">from</span>}
              <span data-testid={`product-price-${product.id}`} className="font-serif text-lg text-forest font-semibold">₹{price}</span>
              {showStrike && <s data-testid={`product-mrp-${product.id}`} className="text-xs text-mutedgreen ml-2">₹{product.price}</s>}
              <span className="block text-[11px] text-mutedgreen">{hasVariants ? `${product.variants.length} pack sizes` : `/ ${product.unit}`}</span>
            </div>
            {hasVariants ? (
              <Link to={`/product/${product.id}`} data-testid={`add-to-cart-${product.id}`} className={`flex items-center gap-1.5 bg-forest text-cream text-[11px] font-semibold uppercase tracking-[0.12em] pl-4 pr-3 py-2.5 rounded-full hover:bg-gold transition-all duration-300 ${available ? "" : "opacity-40 pointer-events-none"}`}>
                {available ? <>Choose <ArrowRight size={13} /></> : "Unavailable"}
              </Link>
            ) : (
              <button
                data-testid={`add-to-cart-${product.id}`}
                onClick={() => add(product.id)}
                disabled={!available}
                className="flex items-center gap-1.5 bg-forest text-cream text-[11px] font-semibold uppercase tracking-[0.12em] pl-4 pr-3 py-2.5 rounded-full hover:bg-gold active:scale-95 transition-all duration-300 disabled:opacity-40 disabled:hover:bg-forest disabled:cursor-not-allowed"
              >
                {available ? <>Add <Plus size={13} /></> : "Unavailable"}
              </button>
            )}
          </div>
        </div>
      </div>
    </StaggerItem>
  );
};
